import React, { useState, useCallback } from "react";
import { Button } from "../ui/button";
import { useSocial } from "../../contexts/SocialContext";
import { useAppState } from "../../contexts/WorkoutContext";
import { Loader2, Radio, RefreshCw, UserPlus } from "lucide-react";
import FeedPostCard from "./FeedPostCard";
import ShareWorkoutDialog from "./ShareWorkoutDialog";
import SharingRow from "./SharingRow";
import LiveNow, { JoinLiveSheet } from "./LiveNow";

export default function FeedTab() {
  const { feedPosts, feedLoading, feedHasMore, loadFeed } = useSocial();
  const { setSocialTab } = useAppState();
  const [refreshing, setRefreshing] = useState(false);
  const [joinOpen, setJoinOpen] = useState(false);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await loadFeed(true);
    setRefreshing(false);
  }, [loadFeed]);

  const handleLoadMore = useCallback(() => {
    loadFeed(false);
  }, [loadFeed]);

  return (
    <div className="mt-4 grid grid-cols-[minmax(0,1fr)] gap-5">
      <div className="grid gap-2">
        <div className="grid grid-cols-2 gap-2">
          <ShareWorkoutDialog />
          <Button variant="outline" onClick={() => setJoinOpen(true)}>
            <Radio className="mr-2 h-4 w-4" /> Join live
          </Button>
        </div>
        {/* Who sees what you post, above the posts: at the foot of an endless
            feed it sat below Load more. */}
        <SharingRow />
      </div>
      <JoinLiveSheet open={joinOpen} onOpenChange={setJoinOpen} />

      <LiveNow />

      {feedLoading && feedPosts.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : feedPosts.length === 0 ? (
        <div className="rounded-xl border border-border bg-card px-4 py-6 text-center">
          <p className="text-sm text-muted-foreground">
            Nothing here yet. Finish a workout and it shows here for your mates,
            and theirs show here for you.
          </p>
          <Button variant="outline" className="mt-4" onClick={() => setSocialTab("mates")}>
            <UserPlus className="mr-2 h-4 w-4" /> Find mates
          </Button>
        </div>
      ) : (
        <section className="grid min-w-0 gap-2">
          <div className="flex items-center justify-between gap-2 px-0.5">
            <h2 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Recent</h2>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs text-muted-foreground"
              onClick={handleRefresh}
              disabled={refreshing}
            >
              <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>
          <div className="space-y-3">
            {feedPosts.map((post) => (
              <FeedPostCard key={post.id} post={post} onUpdate={() => loadFeed(true)} />
            ))}
          </div>
          {feedHasMore && (
            <Button variant="outline" className="mt-1 w-full" onClick={handleLoadMore} disabled={feedLoading}>
              {feedLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Load more
            </Button>
          )}
        </section>
      )}
    </div>
  );
}
